import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import { gameRoundIdAtom, gameStatusState, stopAtState } from '../state';
import { GameStatus } from '../types/crash';
import { DebugBoxWrapper, StyledBetBtn } from './styles';
import { CircularProgress, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { currentCurrencyState, userInfoState } from '@/state/userInfo';
import { useBet, useBetStyle, useCrashOut } from './useCrashBet';
import { GLOBAL_MODALS, globalModals } from '@/state/globalSettings';
import { getCrashDecimal } from '@/constants/decimal';
import { floorToNString } from '@/components/features/casino/crash/GameBox/utils';
import { StyledTokenIcon } from '@/components/ui/common/TokenIcon';
import useBindEmailOrLogin from '@/hooks/useBindEmailOrLogin';

interface BetBtnProps {
  betAmount: number;
  stopAt: number;
  isPc: boolean;
}

const BetBtn: React.FC<BetBtnProps> = ({ stopAt, betAmount, isPc }) => {
  const currentCurrency = useAtomValue(currentCurrencyState);
  const decimal = getCrashDecimal(currentCurrency);
  const { t } = useTranslation();
  const betCurrency = floorToNString(betAmount, decimal);
  const gameRoundId = useAtomValue(gameRoundIdAtom);

  const { requirementToBindEmailOrLogin } = useBindEmailOrLogin();
  // const betCurrency = floorToN(betAmount, 2);
  const { userMetaData } = useAtomValue(userInfoState);
  const userLoggedIn = !!userMetaData?.token;
  const setModals = useSetAtom(globalModals);

  const openLoginModal = () => {
    setModals({
      [GLOBAL_MODALS.LOGIN]: true,
    });
  };

  const [gameStatus] = useAtom(gameStatusState);

  const commonProps = useBetStyle(isPc);

  const {
    hasNextRoundBet,
    nextRoundBetValue,
    hasBet,
    noBet,
    bettedAmount,
    betLoading,
    handleBet,
    handleBetNextRound,
    handleCancel,
  } = useBet({
    userLoggedIn,
    openLoginModal,
    betAmount: betCurrency,
    stopAt,
    gameStatus,
    currentCurrency,
  });
  const [stopAtValue, setStopAtValue] = useAtom(stopAtState);

  const {
    autoCrashOut,
    crashedTime,
    profit,
    multiplier,
    crashLoading,
    handleCrashOut,
  } = useCrashOut({
    noBet,
    bettedAmount,
    gameStatus,
  });

  const disabled = useMemo(() => {
    return !!(
      [GameStatus.Starting, GameStatus.Start, GameStatus.Pending].includes(
        gameStatus,
      ) && hasBet
    );
  }, [gameStatus, hasBet]);

  const router = useRouter();
  const { debug } = router.query;
  const DebugBox = () => {
    const statusStrMap = {
      [GameStatus.Init]: 'Init',
      [GameStatus.Starting]: 'Starting',
      [GameStatus.Pending]: 'Pending',
      [GameStatus.Start]: 'Start',
      [GameStatus.InProgress]: 'InProgress',
      [GameStatus.Crash]: 'Crash',
    };
    const StateBox = ({ value, initValue, setInfo, useInfo, resetInfo }) => {
      const olStyle = {
        marginBottom: 0,
        marginTop: 0,
        marginLeft: -10,
      };
      return (
        <li>
          stopAtState:{value}
          <ol style={olStyle}>
            <b>initial:</b> {initValue}
          </ol>
          <ol style={olStyle}>
            <b>set?</b> {setInfo}
          </ol>
          <ol style={olStyle}>
            <b>use?</b> {useInfo}
          </ol>
          <ol style={olStyle}>
            <b>reset?</b> {resetInfo}
          </ol>
        </li>
      );
    };
    const stopAtStateInfo = {
      value: String(stopAtValue),
      initValue: 'null',
      setInfo: '1. when bet 2. reload page',
      useInfo: 'auto crashout',
      resetInfo: '1.auto crashout 2. disable switch',
    };

    return (
      <>
        {typeof debug !== 'undefined' && (
          <DebugBoxWrapper>
            form value:
            <li>bet:{betAmount}</li>
            <li>stopAt:{stopAt}</li>
            game state:
            <li>gameRoundId:{gameRoundId}</li>
            <li>status:{statusStrMap[gameStatus]}</li>
            state value:
            <StateBox {...stopAtStateInfo}></StateBox>
            <li>nextBettAmount:{nextRoundBetValue} </li>
            <li>betAmount:{bettedAmount} </li>
            <li>crashedTime:{crashedTime}</li>
            <li>autoCrashOut:{autoCrashOut ? 'true' : 'false'}</li>
            <li>noBet:{noBet ? 'true' : 'false'}</li>
          </DebugBoxWrapper>
        )}
      </>
    );
  };

  const NextRoundBtn = useMemo(() => {
    const prop = hasNextRoundBet
      ? {
          onClick: handleCancel,
          text: 'Cancel',
        }
      : {
          onClick: handleBetNextRound,
          text: 'Bet (Next Round)',
        };
    return (
      <StyledBetBtn variant="contained" {...commonProps} {...prop}>
        <Typography variant="h5">{t(prop.text)}</Typography>
        <Typography variant="h6" sx={{ fontSize: '14px', lineHeight: 1 }}>
          {betCurrency}
          <StyledTokenIcon currency={currentCurrency} />
        </Typography>
      </StyledBetBtn>
    );
  }, [
    hasNextRoundBet,
    handleCancel,
    handleBetNextRound,
    commonProps,
    t,
    betAmount,
    betCurrency,
  ]);

  const BtnContent = useMemo(() => {
    // ---1 Countdown
    if (
      [GameStatus.Starting, GameStatus.Pending, GameStatus.Start].includes(
        gameStatus,
      )
    ) {
      return (
        <StyledBetBtn
          variant={'contained'}
          {...commonProps}
          onClick={handleBet}
          disabled={disabled}
        >
          {betLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography variant="h5">{t('Bet')}</Typography>
              <Typography variant="h6" sx={{ fontSize: '14px', lineHeight: 1 }}>
                {/*<MoneyWithCurrencyIcon value={betCurrency} />*/}
                {betCurrency}
                <StyledTokenIcon currency={currentCurrency} />
              </Typography>
            </>
          )}
        </StyledBetBtn>
      );
    } else if (gameStatus === GameStatus.InProgress) {
      //--- 2 running
      //2-1
      if (hasBet && !crashedTime) {
        return (
          <StyledBetBtn
            variant={'contained'}
            {...commonProps}
            onClick={handleCrashOut}
          >
            {crashLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="h5">
                  {t('Cash Out')} @{multiplier}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontSize: '14px', lineHeight: 1 }}
                >
                  {profit}
                  <StyledTokenIcon currency={currentCurrency} />
                </Typography>
              </>
            )}
          </StyledBetBtn>
        );
      } else {
        //2-2
        return NextRoundBtn;
      }
    } else {
      //3 OverGame Settlement
      return NextRoundBtn;
    }
  }, [
    gameStatus,
    betLoading,
    betAmount,
    hasBet,
    nextRoundBetValue,
    crashedTime,
    profit,
    commonProps,
    multiplier,
    betCurrency,
    currentCurrency,
  ]);

  return (
    <>
      {BtnContent}
      <DebugBox></DebugBox>
    </>
  );
};

export default BetBtn;
